/** Минимальные поля услуги, нужные для подписи в фильтре расписания. */
export type ServiceOptionSource = {
  id: number;
  name: string;
  duration_minutes: number;
  price: string;
  price_from?: number | null;
};

const rublesFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

function parsePrice(value: string | number): number | null {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatRubles(amount: number): string {
  return `${rublesFormatter.format(amount)} ₽`;
}

export function formatServiceDuration(minutes: number): string {
  return Number.isFinite(minutes) && minutes > 0 ? `${Math.round(minutes)} мин` : '';
}

export function formatServiceOptionPrice(price: string | number, priceFrom?: number | null): string {
  const base = parsePrice(price);
  const from = priceFrom != null && Number.isFinite(priceFrom) && priceFrom > 0 ? priceFrom : null;
  if (from != null && (base == null || from < base)) {
    return `от ${formatRubles(from)}`;
  }
  return base != null ? formatRubles(base) : '';
}

/**
 * Подписи услуг для фильтра расписания: одинаковые названия различаются длительностью и ценой.
 * Если данные полностью совпадают, добавляется стабильный идентификатор услуги.
 */
export function buildServiceOptionLabels(services: readonly ServiceOptionSource[]): Map<number, string> {
  const nameCounts = new Map<string, number>();
  for (const service of services) {
    nameCounts.set(service.name, (nameCounts.get(service.name) ?? 0) + 1);
  }

  const baseLabels = new Map<number, string>();
  const labelCounts = new Map<string, number>();
  for (const service of services) {
    const parts = [service.name];
    if ((nameCounts.get(service.name) ?? 0) > 1) {
      const duration = formatServiceDuration(service.duration_minutes);
      const price = formatServiceOptionPrice(service.price, service.price_from);
      if (duration) parts.push(duration);
      if (price) parts.push(price);
    }
    const label = parts.join(' · ');
    baseLabels.set(service.id, label);
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }

  const labels = new Map<number, string>();
  for (const service of services) {
    const base = baseLabels.get(service.id) ?? service.name;
    labels.set(service.id, (labelCounts.get(base) ?? 0) > 1 ? `${base} · #${service.id}` : base);
  }
  return labels;
}
